import { Component, OnInit, ViewChild, ElementRef, OnDestroy, AfterViewInit, Input, Output, EventEmitter } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap, first, map } from 'rxjs/operators';
import { indicate } from 'src/app/operators';
import {
  EsraPipingAPIClient,
  ComponentViewModel,
  SearchRequest,
  EsraSearchFilterModel,
  ComponentCatalogSearchViewModel,
  FilterType,
  FilterableProperty
} from 'src/app/shared/models/autogenerated-piping';
import { formatMaterialAstm } from 'src/app/shared/models/material-astm.model';
import { faSearch, faInfo, faPen, faPlusCircle, faArchive, faTrash } from '@fortawesome/free-solid-svg-icons';
import { RangeFilterHelper } from 'src/app/search/pages/common/range-filter.helper';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTable } from '@angular/material/table';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { FilterDataService } from 'src/app/search/services/filter.service';
import { ProductPanelHelper } from 'src/app/shared/components/product-panel/product-panel-helper';
import { MatDialog } from '@angular/material/dialog';
import { ComponentAddEditComponent } from '../dialogs/component-add-edit/component-add-edit.component';
import { UserService } from 'src/app/core/services/user.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { ConfirmDialogComponent } from '../../../../shared/confirm-dialog/confirm-dialog.component';
import { FilterablePropertyFactory } from 'src/app/shared/components/factory/filterable-property-factory.service';
import { productionGuard } from 'src/app/core/guards/guards';
import { ServiceNoteUsageComponent } from '../dialogs/service-note-usage/service-note-usage.component';
import { DictionaryExportService } from '../services/dictionary-export.service';


@Component({
    selector: 'app-component-catalog',
    templateUrl: './component-catalog.component.html',
    styleUrls: ['./component-catalog.component.scss'],
    standalone: false
})
export class ComponentCatalogComponent implements OnInit, OnDestroy, AfterViewInit {
  /** When used inside a modal wrapper, set this to true so the component can behave accordingly */
  @Input() isModal: boolean = false;
  /** Optional initial group value when opened from a modal */
  @Input() initialGroup?: string;
  /** Emits selected items when user confirms from a wrapper/modal */
  @Output() selectionConfirmed: EventEmitter<ComponentViewModel[]> = new EventEmitter<ComponentViewModel[]>();

  // Selection tracking used when component is hosted inside a modal
  private _selectedMap = new Map<number | string, ComponentViewModel>();
  // If set, this will cause the initialGroup to be applied after the first API load
  private _applyInitialGroupAfterFirstLoad: boolean = false;
  private _modalGroupFiltersLoaded: boolean = false;

  orderBy = '';
  totalCount: number;
  wasChecked = false;
  showResult = true;
  allowSlideChange = false;
  searchText = '';
  currentSearchRequest: SearchRequest;
  columnNamesSorted: string[] = [];
  referenceColumnNamesSortedStart: string[] = [
    "group",
    "type",
    "ends",
    "description",
    "componentCode",
    //BELOW ARE UNORDERED per Haitham's request for all attributes to be ordered by their use in creating description attribute"
    "material",
    "astm",
    "const",
    "designSTD",
    "specialRequirements",
    "usedIn",
    "active"
  ];

  referenceColumnNamesSortedEnd: string[] = ["fromRevisionId", "toRevisionId"];

  displayedColumns: string[] = [
    'group',
    'componentCode',
    'type',
    'ends',
    'description',
    'materialAstm',
    'construction',
    'designStandard',
    'specialRequirements',
    'pipeClassRefs',
    'status'
  ];

  /** Canonical column order source used to render filters in the same order as table columns. */
  dictionaryDefinition: { dictionaryItems: Array<{ propertyName: string }> } = {
    dictionaryItems: []
  };

  componentTypes$: Observable<Map<string, ComponentViewModel[]>>;
  searching$ = new BehaviorSubject<boolean>(false);
  loadingFilters$ = new BehaviorSubject<boolean>(false);

  componentGroupsArray: string[];
  staticcomponentGroupsArray: string [];
  nonRenderColumnNames: string[] = ["componentId", "componentClassificationId", "category", "nps", "npsFrom", "npsTo", "schRat", "sch2"];
  componentsArrayFiltered: ComponentViewModel[] = [];
  dataSource = new MatTableDataSource(this.componentsArrayFiltered);
  currentAvailableFilters: EsraSearchFilterModel[];

  selectedType: string = null;
  searchInputString = "";
  showInactive: boolean = false;
  allSelected = false;
  groupSelectionMade = false;
  isUserAdmin = false;
  filterableProperties: FilterableProperty[];

  faInfo = faInfo;
  faEdit = faPen;
  faArchive = faArchive;
  faSearch = faSearch;
  faCreate = faPlusCircle;
  faTrash = faTrash;

  currentPage = 0;
  pageSize = 100;
  pageSizes = [2, 5, 10, 25, 50, 100];

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('searchInput') searchInput: ElementRef;
  @ViewChild(MatTable) matTable: MatTable<ComponentViewModel>;

  // Expose columns to template; add 'select' column when used as modal
  get columnDefs(): string[] {
  if (!this.isModal) return this.displayedColumns;
  // When used as modal, hide admin-only columns
  const hiddenWhenModal = new Set(['editAndArchive', 'status']);
  const filtered = this.displayedColumns.filter(c => !hiddenWhenModal.has(c));
  return ['select', ...filtered];
  }

  // Toggle select all (header checkbox)
  toggleSelectAll(event: MatCheckboxChange) {
    const checked = event.checked;
    if (!this.dataSource || !this.dataSource.data) { return; }
    this.dataSource.data.forEach(row => {
      (row as any).__selected = checked;
      if (checked) {
        this._selectedMap.set(this.getRowKey(row), row);
      } else {
        this._selectedMap.delete(this.getRowKey(row));
      }
    });
  }

  get selectedCount(): number {
    return this._selectedMap.size;
  }

  get selectedItems(): ComponentViewModel[] {
    return Array.from(this._selectedMap.values());
  }

  onRowToggle(row: ComponentViewModel) {
    const key = this.getRowKey(row);
    if ((row as any).__selected) {
      this._selectedMap.set(key, row);
    } else {
      this._selectedMap.delete(key);
    }
  }

  onRemoveRowFromSelection(row: ComponentViewModel) {
    const key = this.getRowKey(row);
    this._selectedMap.delete(key);
    (row as any).__selected = false;
  }

  private getRowKey(row: ComponentViewModel): string {
    const rowRecord: Record<string, unknown> = row as unknown as Record<string, unknown>;
    const componentCodeWithMarkup: string = (rowRecord['componentCode'] as string | undefined) ?? '';
    const componentCode: string = ProductPanelHelper.RemoveHTML(componentCodeWithMarkup).trim();

    if (componentCode) {
      return componentCode;
    }

    const componentId: string = row?.componentId ?? '';
    if (componentId) {
      return componentId;
    }

    return String((row as any)?.tag ?? '');
  }

  // Modal actions
  confirmModalSelection() {
    const selected: ComponentViewModel[] = Array.from(this._selectedMap.values())
      .map((component: ComponentViewModel) => this.withoutHighlightMarkup(component));
    this.selectionConfirmed.emit(selected);
  }

  /**
   * Copies a result row with the search-highlight markup stripped from every string field.
   * `parseHighlightedData` injects `<em class="highlighted">` into all of them for display, so
   * the raw rows must never be handed to a caller that persists them (the markup would end up
   * saved on the pipe class and rendered as literal text).
   * @param component The selected result row.
   * @returns A plain copy safe to pass outside the grid.
   */
  private withoutHighlightMarkup(component: ComponentViewModel): ComponentViewModel {
    const cleaned: ComponentViewModel = new ComponentViewModel(component);
    // Indexed alias so every string field can be stripped without naming them all.
    const cleanedValues: Record<string, unknown> = cleaned as unknown as Record<string, unknown>;

    Object.keys(cleanedValues).forEach((propertyName: string) => {
      const value: unknown = cleanedValues[propertyName];
      if (typeof value === 'string') {
        cleanedValues[propertyName] = ProductPanelHelper.RemoveHTML(value);
      }
    });

    return cleaned;
  }

  cancelModal() {
    // Emit empty to signal cancellation
    this.selectionConfirmed.emit([]);
  }

  // Returns true if the group filter should be read-only (when initialGroup is provided)
  isGroupReadOnly(): boolean {
    return this.initialGroup !== undefined && this.initialGroup !== null && this.initialGroup.trim() !== '';
  }

  constructor(
    public rangeFilterHelper: RangeFilterHelper,
    private esraApiClient: EsraPipingAPIClient,
    private dialog: MatDialog,
    private userService: UserService,
    private snackbarService: SnackBarService,
    private filterDataService: FilterDataService,
    private productPanelHelper: ProductPanelHelper,
    private dictionaryExportService: DictionaryExportService
  ) {}

  /** Export function passed to the shared export button component. Only shown outside modal mode. */
  exportFn = () => this.dictionaryExportService.exportComponentCatalog();

  ngOnInit(): void {
    // Material and ASTM are combined into one "materialAstm" display column, but the backend
    // still exposes them as two independent filterable properties -- keep both in the filter
    // ordering source (in place of the combined column) so their filter chips/panels still
    // render in the expected position instead of falling to the unordered tail.
    const filterOrderColumns: string[] = this.displayedColumns.flatMap((propertyName: string) =>
      propertyName === 'materialAstm' ? ['material', 'astm'] : [propertyName]);
    this.dictionaryDefinition.dictionaryItems = filterOrderColumns.map((propertyName: string) => ({ propertyName }));

    if (productionGuard()) {
      this.displayedColumns.push('editAndArchive');
    }
    this.isUserAdmin = this.isUserInRoleToCreate();
    // If opened with an initialGroup (e.g. from modal wrapper), mark it to be applied
    // after the first API load completes. This prevents applying the filter too early
    // (before initial data/filters are populated) which caused empty results.
    if (this.initialGroup && this.initialGroup.trim() !== '') {
      this.selectedType = this.initialGroup;
      this.groupSelectionMade = true;
      this._applyInitialGroupAfterFirstLoad = true;
    }
    this.filterDataService.currentFiltersSelection$
      .pipe(
        map((filterSelection) => {
          this.filterableProperties = Array.from(filterSelection.values());
          this.currentPage = 0;
          this.orderBy = "";
          this.search();
        })
      )
      .subscribe();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this.filterDataService.removeAllFilters();
  }

  onShowInactiveToggleChange(): void {
    this.currentPage = 0;
    this.search();
  }

  public search(isSortSearch = false): void {
    // When hosted in a modal (e.g. component selection in a revision), silently exclude
    // inactive components. This filter is injected directly into the request and never
    // written to filterableProperties, so it is invisible to the user (no chip, no panel entry).
    const activeOnlyFilter: FilterableProperty = new FilterableProperty({
      propertyName: 'IsActive',
      filterType: FilterType._6,  // FilterType.BOOLEAN — generates: IsActive eq true (exclude inactive)
      filterOptions: ['true'],
      displayName: ''
    });
    const requestFilters: FilterableProperty[] = this.isModal
      ? [...(this.filterableProperties || []), activeOnlyFilter]
      : this.filterableProperties;

    const request: SearchRequest = new SearchRequest({
      perPage: this.pageSize,
      page: this.currentPage,
      mainSearch: this.searchInputString,
      filters: requestFilters,
      orderBy: this.orderBy
    });

    const showAll: boolean = this.isModal ? false : this.showInactive;

    this.esraApiClient.componentCatalogSearch(showAll, request).pipe(
      indicate(this.searching$),
      first(),
      tap(res => {
          this.componentsArrayFiltered = res.results;
          this.parseHighlightedData(this.componentsArrayFiltered);
          this.dataSource = new MatTableDataSource(this.componentsArrayFiltered);
          this.totalCount = res.totalResults;

          //Preserve previously selected components
          this.reapplySelected();

          // Only replace filter options when no checkbox filters are active. When filters
          // are applied, the API returns a narrowed options list based on the current result
          // set — replacing currentAvailableFilters would collapse the visible options and
          // lose checked state in the filter panels (options not present in the filtered
          // results are dropped, so their checkboxes disappear entirely).
          const hasActiveCheckboxFilters: boolean = this.filterableProperties.some(fp => fp.propertyName !== 'Group');

          if (!this.currentAvailableFilters || this.currentAvailableFilters.length == 0) {
            this.currentAvailableFilters = this.orderFiltersByDictionaryItems(res.searchFilters);
          } else if (this.isModal && !this._modalGroupFiltersLoaded && this.groupSelectionMade) {
            this.currentAvailableFilters = this.orderFiltersByDictionaryItems(res.searchFilters);
            this._modalGroupFiltersLoaded = true;
          } else if (!this.isModal && !hasActiveCheckboxFilters) {
            if (res.searchFilters && res.searchFilters.length > 0) {
              this.currentAvailableFilters = this.orderFiltersByDictionaryItems(res.searchFilters);
            }
          }

          this.setInitialTypeFilterOptions();
          this.getDisplayColumns();

          // If we were asked to apply initialGroup after the first load, do it now by
          // setting the group filter into the FilterDataService and re-running a single
          // subsequent search. This avoids pre-filtering before filter options are populated.
          if (this._applyInitialGroupAfterFirstLoad) {
            this._applyInitialGroupAfterFirstLoad = false;
            try {
              const gf = this.getGroupFilter();
              this.filterDataService.setFilterValue(gf);
            } catch (e) {
              console.warn('Failed to apply initialGroup after first load', e);
            }
            // trigger a fresh search using the newly applied filter
            setTimeout(() => this.search(), 0);
            return;
          }

          if (isSortSearch) {
            this.retrySearchWithNoFilters();
          }
        }
      )).subscribe({error: err => console.log("Error: " + err)});
  }

  sortData(sort: Sort) {
    switch(sort.direction) {
      case ('asc'):
        this.orderBy = sort.active;
        break;
      case('desc'):
        this.orderBy = `${sort.active} desc`;
        break;
      default:
        this.orderBy = '';
    }

    // Only update the sort order and re-search — do NOT add a null-exclusion filter here,
    // as that caused rows without a value in the sorted column to be hidden from results.
    this.search();
  }

  retrySearchWithNoFilters() {
    if (this.dataSource.data.length < 1 && !this.wasChecked) {
      this.filterableProperties = [];
      this.wasChecked = true;
      this.search(true);
    } else {
      this.wasChecked = false;
    }
  }

  private reapplySelected(): void {
    if (!this.dataSource || !this.dataSource.data) return;

    this.dataSource.data.forEach(row => {
      const key = this.getRowKey(row);
      if (this._selectedMap.has(key)) {
        (row as any).__selected = true;
      } else {
        (row as any).__selected = false;
      }
    });
  }

  public updatePage(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.search();
  }

  private setInitialTypeFilterOptions(): void {
    if (this.componentGroupsArray !== null) {
      this.componentGroupsArray = this.currentAvailableFilters.find(x => x.filterPropertyName == "Group")?.options ?? [];
    }

    //initializing static array that will always display
    // it seems current avaiable filters gets reduced
    if(!this.staticcomponentGroupsArray){
      this.staticcomponentGroupsArray = [...this.componentGroupsArray];
    }
  }

  /**
   * Orders filters by the table dictionary column order and keeps unknown filters at the end.
   * This keeps filter rendering aligned with visible columns regardless of API/data ordering.
   */
  private orderFiltersByDictionaryItems(filters: EsraSearchFilterModel[] | undefined): EsraSearchFilterModel[] {
    if (!filters || filters.length === 0) {
      return [];
    }

    const dictionaryItems: Array<{ propertyName: string }> = this.dictionaryDefinition?.dictionaryItems ?? [];
    const dictionaryOrder: Map<string, number> = new Map<string, number>();

    dictionaryItems.forEach((item: { propertyName: string }, index: number) => {
      dictionaryOrder.set(this.normalizePropertyKey(item.propertyName), index);
    });

    return [...filters].sort((a: EsraSearchFilterModel, b: EsraSearchFilterModel) => {
      const orderA: number = dictionaryOrder.get(this.normalizePropertyKey(a.filterPropertyName)) ?? Number.MAX_SAFE_INTEGER;
      const orderB: number = dictionaryOrder.get(this.normalizePropertyKey(b.filterPropertyName)) ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
  }

  private normalizePropertyKey(propertyName: string | undefined): string {
    const normalizedPropertyName: string = (propertyName ?? '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

    if (normalizedPropertyName === 'sch1rat') {
      return 'sch1rat';
    }

    if (normalizedPropertyName === 'sch2') {
      return 'sch2';
    }

    if (normalizedPropertyName === 'specialrequirements') {
      return 'specialrequirements';
    }

    if (normalizedPropertyName === 'componentcode') {
      return 'componentcode';
    }

    if (normalizedPropertyName === 'pipeclassreferences' || normalizedPropertyName === 'pipeclassrefs') {
      return 'pipeclassrefs';
    }

    return normalizedPropertyName;
  }

  public selectGroup(event): void {
    this.searchInputString = "";

    this.selectedType = event.value;
    this.showResult = true;
    if (this.selectedType == "All Types") {
      this.groupSelectionMade = false;
      this.allSelected = true;
      this.showResult = true;
      this.filterableProperties = [];
      this.search();
    }
    else {
      this.groupSelectionMade = true;
      this.allSelected = false;
      this.filterDataService.setFilterValue(this.getGroupFilter());
    }
  }

  private getGroupFilter(): FilterableProperty {
    const newSearchRequestFilter: FilterableProperty = FilterablePropertyFactory.createFilterbleProperty(FilterType._1, "", "");
    if (this.groupSelectionMade) {
      newSearchRequestFilter.displayName = "";
      newSearchRequestFilter.propertyName = "Group";
      newSearchRequestFilter.filterType = FilterType._1;
      newSearchRequestFilter.filterOptions = [];
      newSearchRequestFilter.filterOptions.push(this.selectedType);
    } else {
      newSearchRequestFilter.filterOptions = [];
    }
    return newSearchRequestFilter;
  }  getDisplayColumns(): void {
    // Clears existing column names
    const columnNamesRaw = [];
    if (this.componentsArrayFiltered !== undefined && this.componentsArrayFiltered.length > 0 && this.componentsArrayFiltered[0] !== undefined && this.componentsArrayFiltered[0] !== null) {
      const firstComponent = this.componentsArrayFiltered[0];
      let property: keyof typeof firstComponent;
      for (property in firstComponent) {
        if (!this.nonRenderColumnNames.includes(property)) {
          columnNamesRaw.push(property);
        }
      }
    }
    this.columnNamesSorted = this.getColumnNamesCustomSorted(columnNamesRaw);
  }

  private getColumnNamesCustomSorted(columnNames: string[]): string[] {
    const sortedColumnNames: string[] = [];
    //populates sortedColumnNames with attributes that should be rendered first (left side of table)
    for (const refColName of this.referenceColumnNamesSortedStart) {
      for (const columnName of columnNames) {
        if (columnName == refColName) {
          sortedColumnNames.push(columnName);
          columnNames.splice(columnNames.indexOf(columnName), 1);
          break;
        }
      }
    }
    //populates sortedColumnNames with any attribute that is not explicitly defined
    for (const columnName of columnNames) {
      if (this.referenceColumnNamesSortedEnd.indexOf(columnName) == -1) {
        sortedColumnNames.push(columnName);
        columnNames.splice(columnNames.indexOf(columnName), 1);
      }
    }
    //populates sortedColumnNames with attributes that shoudl be rendered last (right side of table)
    for (const refColName of this.referenceColumnNamesSortedEnd) {
      for (const columnName of columnNames) {
        if (columnName == refColName) {
          sortedColumnNames.push(columnName);
          columnNames.splice(columnNames.indexOf(columnName), 1);
          break;
        }
      }
    }

    sortedColumnNames.push("actions");
    return sortedColumnNames;
  }

  clearButton(): void {
    this.searchInputString = "";
    this.search();
  }
  searchByButton(): void {
    this.orderBy = "";
    this.showResult = true;
    this.search();
  }

  searchInputKeyupEnter(): void {
    this.searchByButton();
  }

  public clearAll(): void {
    if (this.initialGroup && this.initialGroup.trim() !== '') return this.clearAllFiltersExceptGroup();

    this.selectedType = undefined;
    this.groupSelectionMade = false;
    this.searchInputString = "";
    this.currentPage = 0;
    this.orderBy = "";
    this.showResult = false;
    this.getAllSearch();
  }

  public getAllSearch(): void {
    this.filterableProperties = [];
    this.searchInputString = "";
    this.search();
  }

  public updateSearchInputString(event): void {
    this.searchInputString = event.target.value;
  }

  public isUserInRoleToCreate(): boolean {
    return this.userService.isRoleExists('Admin');
  }

  public createNew() {
    const dialogRef = this.dialog.open(ComponentAddEditComponent, {
      width: '60%',
      height: '95%'
    });
    dialogRef.afterClosed().subscribe(newComponent => {
      if (newComponent) {
        this.esraApiClient.createComponent(newComponent)
          .pipe(indicate(this.searching$), first())
          .subscribe({
            next: (created) => {
              const code = created?.code ?? '';
              this.snackbarService.showSnackBar(
                true,
                `The component was created${code ? ' with code: ' + code : ''}. It may take a few minutes for the table to reflect your changes.`,
                undefined,
                undefined,
                12000
              );
              this.search();
            },
            error: (err) => {
              const responseBody: string = err?.response ?? '';
              const isDuplicate = responseBody.toLowerCase().includes('already exists');
              this.snackbarService.showSnackBar(
                true,
                isDuplicate
                  ? responseBody
                  : 'An error occurred while creating the component. Please try again.',
                undefined,
                undefined,
                10000
              );
            }
          })
      }
    })
  }

  protected editItem(item: ComponentViewModel) {
    // The search result rows have HTML highlight markup (<em class="highlighted">) injected
    // into every string field by parseHighlightedData, and reflect a possibly-stale copy of
    // the search index. Rather than reusing that row object, re-fetch the component directly
    // from the source system by id so the edit form always starts from the current persisted values.
    const componentId: string | undefined = item?.componentId;
    if (!componentId) {
      return;
    }

    this.esraApiClient.getComponentById(componentId)
      .pipe(indicate(this.searching$), first())
      .subscribe({
        next: (component) => {
          const dialogRef = this.dialog.open(ComponentAddEditComponent, {
            width: '40%',
            height: '90%',
            data: component
          });
          dialogRef.afterClosed().subscribe(updateComponent => {
            if (updateComponent) {
              this.esraApiClient.updateComponent(updateComponent)
                .pipe(indicate(this.searching$),
                  first())
                .subscribe({
                  next: (updatedComponent: ComponentViewModel) => {
                    this.applyUpdatedComponentToVisibleRow(updatedComponent, item);

                    const updatedCode: string = (updatedComponent?.code ?? '').trim();
                    this.snackbarService.showSnackBar(
                      true,
                      updatedCode ? `Component modified. New code: ${updatedCode}` : 'Component modified'
                    );

                    if (this.currentSearchRequest !== undefined) {
                      this.search();
                    }
                  },
                  error: (err) => {
                    const responseBody: string = typeof err?.response === 'string' ? err.response : '';
                    const isConflict: boolean = err?.status === 409;
                    this.snackbarService.showSnackBar(
                      true,
                      isConflict
                        ? (responseBody || 'A component with this code already exists.')
                        : err,
                      undefined,
                      undefined,
                      isConflict ? 10000 : undefined
                    );
                 },
                })
            }
          })
        },
        error: () => {
          this.snackbarService.showSnackBar(true, 'Unable to load the component for editing. Please try again.');
        }
      });
  }

  private applyUpdatedComponentToVisibleRow(updatedComponent: ComponentViewModel, originalRow: ComponentViewModel): void {
    const componentId: string = updatedComponent?.componentId ?? originalRow?.componentId ?? '';
    if (!componentId) {
      return;
    }

    const rowToUpdate: ComponentViewModel | undefined = this.componentsArrayFiltered
      .find((row: ComponentViewModel) => (row?.componentId ?? '') === componentId);

    if (!rowToUpdate) {
      return;
    }

    const rowRecord: Record<string, unknown> = rowToUpdate as unknown as Record<string, unknown>;
    rowRecord['componentCode'] = this.highlightText(updatedComponent?.code ?? '');
    rowRecord['description'] = this.highlightText(updatedComponent?.description ?? '');
    rowRecord['group'] = this.highlightText(updatedComponent?.group ?? '');
    rowRecord['type'] = this.highlightText(updatedComponent?.type ?? '');
    rowRecord['material'] = this.highlightText(updatedComponent?.material ?? '');
    rowRecord['astm'] = this.highlightText(updatedComponent?.astm ?? '');
    rowRecord['construction'] = this.highlightText(updatedComponent?.construction ?? '');
    rowRecord['designStandard'] = this.highlightText(updatedComponent?.designStandard ?? '');
    rowRecord['specialRequirements'] = this.highlightText(updatedComponent?.specialRequirements ?? '');
    rowRecord['ends'] = this.highlightText(updatedComponent?.end ?? '');
    rowRecord['isActive'] = updatedComponent?.isActive;

    this.dataSource.data = [...this.componentsArrayFiltered];
  }

  onArchiveClick(component: ComponentViewModel) {
    const componentId = component["componentId"];
    const hasPipeClasses = (component as any)['hasPipeClasses'];

    if (hasPipeClasses) {
      const hasPipeClassesErrorMessage =
        `The component, ${componentId}, could not be archived because it has associated pipe classes.`;
      this.snackbarService.showError(hasPipeClassesErrorMessage);
      return;
    }

    if (component.isReferenced) {
      const isReferencedErrorMessage =
        `The component, ${componentId}, could not be archived because it is referenced by pipe classes.`;
      this.snackbarService.showError(isReferencedErrorMessage);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '30%',
      height: '30%',
      data: {
        data: component,
        dialogMessage: 'Are you sure you want to archive this item?'
      }
    });

    dialogRef.afterClosed().subscribe({
      next: (confirm) => {
        if (confirm) {
          this.archiveComponent(componentId);
        }
      },
      error: (err) => {
        this.snackbarService.showError(err);
      }
    });
  }

  /**
   * Checks if a component can be deleted/archived.
   * Returns true if the component has no associated pipe classes.
   */
  canDeleteComponent(component: ComponentViewModel): boolean {
    const hasPipeClasses = (component as any)['hasPipeClasses'];
    return !hasPipeClasses;
  }

  canEditComponent(component: ComponentViewModel): boolean {
    return component?.isActive !== false;
  }

  protected deleteComponent(component: ComponentViewModel): void {
    const componentId = component["componentId"];
    const hasPipeClasses = (component as any)['hasPipeClasses'];

    if (hasPipeClasses) {
      const hasPipeClassesErrorMessage =
        `The component, ${componentId}, could not be deleted because it has associated pipe classes.`;
      this.snackbarService.showError(hasPipeClassesErrorMessage);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '30%',
      height: '30%',
      data: {
        data: component,
        dialogMessage: 'Are you sure you want to delete this item?'
      }
    });

    dialogRef.afterClosed().subscribe({
      next: (confirm) => {
        if (confirm) {
          this.archiveComponent(componentId);
        }
      },
      error: (err) => {
        this.snackbarService.showError(err);
      }
    });
  }

  updateStatus(component: ComponentViewModel) {
    const newActiveStatus = component.isActive;

    let snackBarMessage = '';
    if (!newActiveStatus) {
      snackBarMessage =
        `You've deactivated a ${component.group} component. Please allow a few minutes for your changes to take effect.`;
    } else {
      snackBarMessage =
        `You've activated a ${component.group} component. Please allow a few minutes for your changes to take effect.`;
    }

    this.esraApiClient.updateComponentStatus(component.componentId, component.isActive)
          .pipe(indicate(this.searching$))
          .subscribe({
            next: () => {
              this.snackbarService.showSnackBar(true, snackBarMessage);
            },
            error: err => {
              this.snackbarService.showSnackBar(true, err);
              setTimeout(this.search, 3000);
            }
          });
  }

  private archiveComponent(componentId: string) {
    this.esraApiClient.archiveComponent(componentId)
      .pipe(
        indicate(this.searching$),
        first()
      )
      .subscribe({
        next: () => {
          this.snackbarService.showSnackBar(true, 'Component archived successfully');
          if (this.currentSearchRequest !== undefined) {
            this.search();
          }
        },
        error: (err) => {
          const errorMessage = err?.response || 'An error occurred while archiving the component.';
          this.snackbarService.showSnackBar(true, errorMessage);
        },
      });
  }

  public toggleAllSelection(): void {
    this.getAllSearch();
  }

  public sortColumn(event): void {
    const columName: string = event.active;
    const pascalCasing = columName.charAt(0).toUpperCase() + columName.substring(1, columName.length);
    this.orderBy = `${pascalCasing} ${event.direction}`;
    this.search();
  }

  latestFilterSelection$ = new BehaviorSubject<string>('');

  updateCheckboxFilterSelection(
    event: Set<string>,
    filterablePropertyName: string,
    filterDiplayName: string
  ) {
    this.latestFilterSelection$.next(filterablePropertyName);
    this.showResult = true;
    if (event.size === 0) {
      this.filterDataService.removeAllFilterValues(filterablePropertyName);
    } else {
      const filterableProperty = FilterablePropertyFactory.createFilterbleProperty(
        1,
        filterablePropertyName,
        filterDiplayName
      );

      filterableProperty.filterOptions = Array.from(event);
      this.filterDataService.setFilterValue(filterableProperty);
    }
  }

  popClearedFilter($event: any, filterPropertyName: string) {
    if (this.latestFilterSelection$.getValue() === filterPropertyName) {
      this.latestFilterSelection$.next('');
    }
  }

  /** Removes a single filter option chip and triggers a new search via FilterDataService. */
  removedChip(propertyName: string, option: string): void {
    const filterableProperties: FilterableProperty[] = this.filterableProperties.filter(
      property => property.propertyName === propertyName
    );
    if (filterableProperties && filterableProperties.length === 1) {
      const filterableProperty: FilterableProperty = filterableProperties[0];
      filterableProperty.filterOptions = filterableProperty.filterOptions.filter(
        filterOption => filterOption !== option
      );
      this.filterDataService.removeSingleFilterValue(propertyName, option);
    }
  }

  parseHighlightedData(data: ComponentCatalogSearchViewModel[]) {
    if (data && data.length > 0) {
      data.forEach(element => {
        for (const key in element) {
          element[key] = this.highlightText(element[key]);
        }
      });
    }
  }

  highlightText(value: unknown) {
    if (typeof value === 'string') {
      return this.productPanelHelper.strReplaceHighlightHtml(value.toString());
    } else {
      return value;
    }
  }

  public stripEmphasisClassFromString(string: string): string {
    return ProductPanelHelper.RemoveHTML(string);
  }

  /**
   * Builds the combined "Material, ASTM" display text for the Material/ASTM column.
   * Material and ASTM are stored as one backend property but still shown as two independent
   * filters, so the grid keeps a single formatted column while filtering stays per-field.
   * @param component The row's component (fields may carry embedded search-highlight markup).
   * @returns HTML-safe display string, e.g. `CS, ASTM A105N`, `CS`, `ASTM A105N`, or `''`.
   */
  public materialAstmDisplay(component: ComponentCatalogSearchViewModel | ComponentViewModel): string {
    return formatMaterialAstm(component?.material, component?.astm);
  }

  protected getUsageInformation(component: ComponentViewModel) {
    this.dialog.open(ServiceNoteUsageComponent, {
      width: '40%',
      height: '40%',
      data: component
    });
  }

  private clearAllFiltersExceptGroup(): void {
    this.currentPage = 0;
    this.orderBy = "";

    const filterToDelete = this.filterableProperties.map(fp => fp.propertyName).filter(pn => pn !== 'Group');

    filterToDelete.forEach(filterName => {
      this.filterDataService.removeAllFilterValues(filterName);
    });

    this.filterDataService.getResetSubject().next(true);
    this.search();
  }
}
